<template>
    <Head title="Cart"/>
    <StoreLayout>
        <PageTitle title="Shopping Cart">
            <div class="flex items-center gap-4" v-if="cart.products.length">
                <button v-if="hasDesignWallProducts" class="text-sm"
                        :disabled="addingToDesignWall" @click="addAllToDesignWall">
                    <PhSquaresFour weight="fill" class="h-4 w-4 inline-block" aria-hidden="true"/>
                    Add cart to design wall
                </button>
                <Link class="text-sm" :href="route('cart.empty')">
                    <PhTrash weight="fill" class="h-4 w-4 inline-block" aria-hidden="true"/>
                    Empty Cart
                </Link>
            </div>
        </PageTitle>
        <Container>
            <template v-if="cart.products.length">
                <div class="mt-4 lg:grid lg:grid-cols-12 lg:items-start lg:gap-x-12 xl:gap-x-16">
                    <section aria-labelledby="cart-heading" class="lg:col-span-7">
                        <h2 id="cart-heading" class="sr-only">Items in your shopping cart</h2>

                        <div class="rounded-md bg-yellow-50 p-4" v-if="cartWarnings > 0">
                            <div class="flex justify-items-center items-center">
                                <PhWarning class="h-8 w-8 text-yellow-600" aria-hidden="true"/>
                                <div class="ml-3">
                                    <p class="text-sm font-medium text-yellow-800"><span
                                        class="font-bold">Heads up!</span>
                                        Some items in your cart are no longer available in the original quantity.
                                    </p>
                                </div>
                                <div class="ml-auto pl-3">
                                    <div class="-mx-1.5 -my-1.5">
                                        <button type="button"
                                                class="inline-flex rounded-md bg-yellow-50 p-1.5 text-yellow-500 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-yellow-600 focus:ring-offset-2 focus:ring-offset-yellow-50"
                                                @click="dismissCartWarning">
                                            <span class="sr-only">Dismiss</span>
                                            <PhX class="h-5 w-5" aria-hidden="true"/>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="rounded-md bg-gray-50 p-4" v-else-if="cart.freeShipping">
                            <div class="flex justify-items-center items-center">
                                <PhTruck weight="fill" class="h-8 w-8 text-indigo-500 flex-shrink-0"
                                         aria-hidden="true"/>
                                <div class="ml-3 flex-auto">
                                    <div class="w-full bg-gray-200 rounded-full h-4 mt-1">
                                        <div class="bg-black h-4 rounded-full"
                                             :style="'width: ' + cart.freeShipping.progress + '%'"></div>
                                    </div>
                                    <div class="mt-2 ml-1 text-gray-800">
                                        <p>You're
                                            <template v-if="cart.freeShipping.only">only</template>
                                            <span class="font-bold">${{
                                                    Math.ceil(cart.freeShipping.left / 100)
                                                }}</span>
                                            away from free shipping.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <ul role="list" class="divide-y divide-gray-200 px-0 lg:px-8">
                            <!-- Grouped items -->
                            <template v-if="hasGroups">
                                <!-- Grouped fabric line items -->
                                <li v-for="group in groupedData.groups" :key="group.key"
                                    class="py-6">
                                    <div class="flex flex-col md:flex-row gap-4">
                                        <div class="flex flex-row flex-1 gap-4 items-stretch">
                                            <!-- Stacked images -->
                                            <div class="flex-shrink-0 relative h-24 w-24 sm:h-36 sm:w-36">
                                                <template v-for="(product, index) in group.products.slice(0, 4)" :key="product.id">
                                                    <img :src="imageSize(product.imageSrc, 300)" :alt="product.title"
                                                         class="absolute h-16 w-16 sm:h-28 sm:w-28 rounded-md object-cover object-center border-2 border-white shadow-sm"
                                                         :style="{
                                                             top: `${index * 8}px`,
                                                             left: `${index * 8}px`,
                                                             zIndex: index
                                                         }"/>
                                                </template>
                                            </div>
                                            <div class="flex flex-row justify-between flex-1">
                                                <div>
                                                    <h3 class="font-medium text-gray-900">{{ group.fabricLine.name }}</h3>
                                                    <p class="mt-1 text-gray-500">{{ group.fabricLine.manufacturer }}</p>
                                                    <div class="hidden md:flex mt-2 items-center gap-3">
                                                        <span class="bg-gray-800 text-white text-xs px-2 py-1 rounded-md">
                                                            {{ group.products.length }} fabrics
                                                        </span>
                                                        <button @click="toggleGroup(group.fabricLine.id)"
                                                                class="text-sm text-indigo-600 hover:text-indigo-500 inline-flex items-center gap-1">
                                                            <PhCaretDown v-if="!isGroupExpanded(group.fabricLine.id)" class="h-4 w-4" />
                                                            <PhCaretUp v-else class="h-4 w-4" />
                                                            {{ isGroupExpanded(group.fabricLine.id) ? 'Hide' : 'Show' }}
                                                        </button>
                                                        <button @click="ungroupFabricLine(group.fabricLine.id)"
                                                                class="text-sm text-gray-500 hover:text-gray-700">
                                                            Ungroup
                                                        </button>
                                                    </div>
                                                </div>
                                                <div class="flex md:hidden flex-col items-center gap-1 mr-4">
                                                    <span class="bg-gray-800 text-white text-xs px-2 py-1 rounded-md">
                                                        {{ group.products.length }} fabrics
                                                    </span>
                                                    <button @click="toggleGroup(group.fabricLine.id)"
                                                            class="text-sm text-indigo-600 hover:text-indigo-500 inline-flex items-center gap-1">
                                                        <PhCaretDown v-if="!isGroupExpanded(group.fabricLine.id)" class="h-4 w-4" />
                                                        <PhCaretUp v-else class="h-4 w-4" />
                                                        {{ isGroupExpanded(group.fabricLine.id) ? 'Hide' : 'Show' }}
                                                    </button>
                                                    <button @click="ungroupFabricLine(group.fabricLine.id)"
                                                            class="text-sm text-gray-500 hover:text-gray-700">
                                                        Ungroup
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="flex flex-col h-full items-center justify-between gap-y-3">
                                            <InputQuantity class="flex-1"
                                                           :quantity="group.quantity"
                                                           :max-available="group.minAvailable"
                                                           :partial-quantities="true"
                                                           @save="(v, done) => saveGroup(group, v, done)">
                                                <button type="button"
                                                        class="mt-2 flex justify-center w-full text-sm font-medium"
                                                        @click="removeGroup(group)">
                                                    <span>Remove All</span>
                                                </button>
                                            </InputQuantity>
                                            <div class="md:order-first">
                                                <Price class="text-lg" :value="group.subtotal"/>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Expanded individual items -->
                                    <div v-if="isGroupExpanded(group.fabricLine.id)" class="mt-4 ml-8 border-l-2 border-gray-200 pl-4 space-y-4">
                                        <div v-for="product in group.products" :key="product.id"
                                             class="flex flex-col sm:flex-row gap-4 py-2">
                                            <div class="flex flex-row flex-1 gap-4">
                                                <Link :href="product.href" class="flex-shrink-0">
                                                    <img :src="imageSize(product.imageSrc, 300)" :alt="product.title"
                                                         class="h-16 w-16 rounded-md object-cover object-center"/>
                                                </Link>
                                                <div class="flex-1">
                                                    <h4 class="text-sm font-medium text-gray-900">
                                                        <a :href="product.href">{{ product.title }}</a>
                                                    </h4>
                                                    <p class="text-sm text-gray-500">{{ product.manufacturerCode ?? product.id }}</p>
                                                    <PriceWithUnits :product="product" class="text-sm"/>
                                                </div>
                                            </div>
                                            <div class="flex items-center gap-4">
                                                <span class="text-sm text-gray-600">{{ product.cart.quantity }} {{ product.uom.stock }}</span>
                                                <Price class="text-sm" :value="product.cart.subtotal"/>
                                                <button type="button"
                                                        class="text-sm text-red-600 hover:text-red-500"
                                                        @click="addToCart(product.productId, 0)">
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                                <!-- Ungrouped products in grouped view -->
                                <li v-for="product in groupedData.ungroupedProducts" :key="product.id"
                                    class="flex flex-col md:flex-row py-6 flex-wrap gap-4">
                                    <div class="flex flex-row flex-1 gap-4">
                                        <div class="flex-shrink-0 relative">
                                            <Link :href="product.href">
                                                <img :src="imageSize(product.imageSrc, 300)" :alt="product.title"
                                                     :class="product.cropImage ? 'object-cover' : 'object-contain'"
                                                     class="h-24 w-24 rounded-md object-center sm:h-48 sm:w-48"/>
                                            </Link>
                                        </div>
                                        <div class="w-full h-full flex flex-col justify-between">
                                            <div>
                                                <h3 class="font-medium text-gray-900">
                                                    <a :href="product.href">{{ product.title }}</a>
                                                </h3>
                                                <p class="mt-1 text-gray-500">{{ product.subtitle }}</p>
                                                <p class="mt-1 text-gray-500">{{ product.manufacturerCode ?? product.id }}</p>
                                                <p>
                                                    <PriceWithUnits :product="product"/>
                                                </p>
                                            </div>
                                            <div>
                                                <StockDetail :product="product" class="flex mt-4"/>
                                                <div class="flex-row flex" v-if="product.cart.desiredQuantity">
                                                    <PhWarning weight="fill"
                                                               class="h-5 w-5 flex-shrink-0 text-yellow-600"
                                                               aria-hidden="true"/>
                                                    <p class="ml-2 text-sm text-gray-500">Original quantity
                                                        ({{ product.cart.desiredQuantity }}) no longer available.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="flex flex-col h-full items-center justify-between gap-y-3">
                                        <template v-if="product.inStock">
                                            <InputQuantity class="flex-1"
                                                           :quantity="product.cart.quantity"
                                                           :max-available="product.quantityAvailable"
                                                           :partial-quantities="product.partialQuantities"
                                                           @save="(v, done) => addToCart(product.productId, v, done)">
                                                <button type="button"
                                                        class="mt-2 flex justify-center w-full text-sm font-medium"
                                                        @click="addToCart(product.productId, 0)">
                                                    <span>Remove</span>
                                                </button>
                                            </InputQuantity>
                                        </template>
                                        <span v-else class="block text-sm text-gray-400">
                                            No Longer Available
                                        </span>
                                        <div class="md:order-first">
                                            <Price class="text-lg" :value="product.cart.subtotal"/>
                                        </div>
                                    </div>
                                </li>
                            </template>
                            <!-- Single/ungrouped view (original) -->
                            <template v-else>
                                <li v-for="product in cart.products" :key="product.id"
                                    class="flex flex-col md:flex-row py-6 flex-wrap gap-4">
                                    <div class="flex flex-row flex-1 gap-4">
                                        <div class="flex-shrink-0 relative">
                                            <Link :href="product.href">
                                                <img :src="imageSize(product.imageSrc, 300)" :alt="product.title"
                                                     :class="product.cropImage ? 'object-cover' : 'object-contain'"
                                                     class="h-24 w-24 rounded-md object-center sm:h-48 sm:w-48"/>
                                            </Link>
                                        </div>
                                        <div class="w-full h-full flex flex-col justify-between">
                                            <div>
                                                <h3 class="font-medium text-gray-900">
                                                    <a :href="product.href">{{ product.title }}</a>
                                                </h3>
                                                <p class="mt-1 text-gray-500">{{ product.subtitle }}</p>
                                                <p class="mt-1 text-gray-500">{{ product.manufacturerCode ?? product.id }}</p>
                                                <p>
                                                    <PriceWithUnits :product="product"/>
                                                </p>
                                            </div>
                                            <div>
                                                <StockDetail :product="product" class="flex mt-4"/>
                                                <div class="flex-row flex" v-if="product.cart.desiredQuantity">
                                                    <PhWarning weight="fill"
                                                               class="h-5 w-5 flex-shrink-0 text-yellow-600"
                                                               aria-hidden="true"/>
                                                    <p class="ml-2 text-sm text-gray-500">Original quantity
                                                        ({{ product.cart.desiredQuantity }}) no longer available.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="flex flex-col h-full items-center justify-between gap-y-3">
                                        <template v-if="product.inStock">
                                            <InputQuantity class="flex-1"
                                                           :quantity="product.cart.quantity"
                                                           :max-available="product.quantityAvailable"
                                                           :partial-quantities="product.partialQuantities"
                                                           @save="(v, done) => addToCart(product.productId, v, done)">
                                                <button type="button"
                                                        class="mt-2 flex justify-center w-full text-sm font-medium"
                                                        @click="addToCart(product.productId, 0)">
                                                    <span>Remove</span>
                                                </button>
                                            </InputQuantity>
                                        </template>
                                        <span v-else class="block text-sm text-gray-400">
                                            No Longer Available
                                        </span>
                                        <div class="md:order-first">
                                            <Price class="text-lg" :value="product.cart.subtotal"/>
                                        </div>
                                    </div>
                                </li>
                            </template>
                        </ul>
                    </section>
                    <!-- Order summary -->
                    <section aria-labelledby="summary-heading"
                             class="mt-2 lg:mt-0 rounded-lg bg-gray-50 px-4 py-6 sm:p-6 lg:col-span-5 lg:p-8">
                        <h2 id="summary-heading" class="text-lg font-medium text-gray-900">Order summary</h2>
                        <CartSummary :cart="cart" :estimate="true" :editable="true"/>
                        <div class="mt-6 text-center">
                            <PrimaryLink v-if="cartWarnings === 0 && purchasableCount > 0" :href="route('checkout.show')" class="w-full text-white !text-xl">
                                Checkout
                            </PrimaryLink>
                            <div v-else class="rounded-md bg-yellow-50 p-4 text-left">
                                <div class="flex items-start">
                                    <PhWarning weight="fill" class="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" aria-hidden="true"/>
                                    <p class="ml-2 text-sm text-yellow-800">
                                        <template v-if="purchasableCount > 0">
                                            Some items are no longer available in the original quantity.
                                        </template>
                                        <template v-else>
                                            None of these items are available anymore.
                                        </template>
                                    </p>
                                </div>
                                <button type="button"
                                        @click="dismissCartWarning"
                                        class="mt-3 w-full inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-semibold bg-yellow-600 text-white hover:bg-yellow-500 transition">
                                    <template v-if="purchasableCount > 0">Accept &amp; Continue to Checkout</template>
                                    <template v-else>Remove unavailable items</template>
                                </button>
                            </div>
                            <!--                            <Link :href="route('cart.empty')" class="w-full text-xs">-->
                            <!--                                Empty cart and start over-->
                            <!--                            </Link>-->
                            <Link :href="route('products.browse')"
                                  class="whitespace-nowrap rounded-full inline-block mt-4 px-4 py-2.5 text-sm font-semibold text-gray-900">
                                Continue shopping
                            </Link>
                        </div>
                    </section>
                </div>
                <Row class="mt-12" title="Recommended Products" :products="recommended"/>
            </template>
            <template v-else>
                <div
                    class="max-w-6xl mx-auto px-6 py-12 lg:px-0">
                    <Empty :icon="PhShoppingCartSimple" title="Your cart is empty." description="We've got high-quality quilting fabric, top-notch customer
                        service, and great prices!">
                        <ShopLink :href="route('products.browse', 'fabric')">
                            Browse popular fabric
                        </ShopLink>
                    </Empty>
                </div>
            </template>
        </Container>
    </StoreLayout>
</template>

<script setup>
import {computed, onMounted, onBeforeMount, ref, watch} from 'vue';
import {Head, Link, router} from '@inertiajs/vue3';
import {PhShoppingCartSimple, PhTruck, PhWarning, PhX, PhTrash, PhCaretDown, PhCaretUp, PhSquaresFour} from '@phosphor-icons/vue';
import StoreLayout from '@/Layouts/StoreLayout.vue';
import PriceWithUnits from "@/Components/PriceWithUnits.vue";
import Price from "@/Components/Price.vue";
import Container from "@/Components/Container.vue";
import InputQuantity from "@/Components/InputQuantity.vue";
import StockDetail from "@/Components/StockDetail.vue";
import CartSummary from "@/Components/CartSummary.vue";
import PrimaryLink from "@/Components/PrimaryLink.vue";
import Row from "@/Pages/Product/Partials/Row.vue";
import PageTitle from "@/Components/PageTitle.vue";
import {imageSize, addToCart, productAsAnalyticItem} from "@/mixins";
import {event} from "vue-gtag";
import Empty from "@/Components/Empty.vue";
import ShopLink from "@/Components/ShopLink.vue";

const props = defineProps({
    cart: Object,
    recommended: Array,
});

const expandedGroups = ref({});
const ungroupedFabricLines = ref({});

onBeforeMount(() => {
    const stored = localStorage.getItem('ungroupedFabricLines');
    if (stored) {
        try {
            ungroupedFabricLines.value = JSON.parse(stored);
        } catch (e) {
            ungroupedFabricLines.value = {};
        }
    }
});

const groupedData = computed(() => {
    const products = props.cart.products;
    const groupMap = new Map();

    for (const product of products) {
        if (!product.fabricLine?.id) continue;
        if (!product.partialQuantities) continue;
        if (ungroupedFabricLines.value[product.fabricLine.id]) continue;

        const key = `${product.fabricLine.id}-${product.cart.quantity}`;
        if (!groupMap.has(key)) {
            groupMap.set(key, {
                fabricLine: product.fabricLine,
                quantity: product.cart.quantity,
                products: [],
                subtotal: 0,
            });
        }
        const group = groupMap.get(key);
        group.products.push(product);
        group.subtotal += product.cart.subtotal;
    }

    const groups = [];
    const groupedProductIds = new Set();

    for (const [key, group] of groupMap) {
        if (group.products.length >= 2) {
            group.minAvailable = Math.min(...group.products.map(p => p.quantityAvailable));
            group.key = key;
            groups.push(group);
            group.products.forEach(p => groupedProductIds.add(p.productId));
        }
    }

    const ungroupedProducts = products.filter(p => !groupedProductIds.has(p.productId));

    return { groups, ungroupedProducts };
});

const hasGroups = computed(() => groupedData.value.groups.length > 0);

function toggleGroup(fabricLineId) {
    expandedGroups.value[fabricLineId] = !expandedGroups.value[fabricLineId];
}

function isGroupExpanded(fabricLineId) {
    return !!expandedGroups.value[fabricLineId];
}

function saveGroup(group, quantity, done) {
    router.patch(route('cart.patch'), group.products.map(p => ({
        productId: p.productId,
        quantity: quantity,
    })), {
        preserveScroll: true,
        preserveState: true,
        onSuccess: done,
        onError: done,
    });
}

function removeGroup(group) {
    router.patch(route('cart.patch'), group.products.map(p => ({
        productId: p.productId,
        quantity: 0,
    })), {
        preserveScroll: true,
        preserveState: true,
    });
}

function ungroupFabricLine(fabricLineId) {
    ungroupedFabricLines.value[fabricLineId] = true;
    localStorage.setItem('ungroupedFabricLines', JSON.stringify(ungroupedFabricLines.value));
}

const cartWarnings = computed(() => props.cart.products.filter((product) => product.cart.desiredQuantity).length);

// Checkout is gated on what can still be bought, not on the warning count: a cart where
// every line fell short is still checkout-able once the shopper accepts the new quantities.
const purchasableCount = computed(() => props.cart.products.filter((product) => product.inStock && product.cart.quantity > 0).length);

const designWallProducts = computed(() => props.cart.products.filter(p => p.designWall));
const hasDesignWallProducts = computed(() => designWallProducts.value.length > 0);
const addingToDesignWall = ref(false);

function addAllToDesignWall() {
    addingToDesignWall.value = true;
    router.patch(route('design-wall.bulk'), designWallProducts.value.map(p => ({
        productId: p.productId,
    })), {
        preserveScroll: true,
        onFinish: () => addingToDesignWall.value = false,
    });
}

function dismissCartWarning() {
    router.delete(route('cart.dismiss'), {
        preserveScroll: true,
        preserveState: true,
    });
}

onMounted(() => {
    setTimeout(() => {
        event('view_cart', {
            currency: "USD",
            value: props.cart.subtotal / 100,
            free_shipping: props.cart.shipping === 0,
            items: props.cart.products.map((product, i) => {
                return {
                    ...productAsAnalyticItem(product, i),
                    quantity: Math.ceil(product.cart.quantity),
                };
            }),
        });
    }, 100);
});

</script>
